import React, { useEffect, useState } from "react";
import { Label } from "../../../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Button } from "../../../../../components/ui/button";
import { SignatureDialog } from "../../../../../components/ui/SignatureDialog";
import { MessageSquare, Plus, PenTool, FileSignature, Trash2 } from "lucide-react";

interface SignatureProps {
  customerSignature: string | null;
  qcSignature: string | null;
  customerRepresentativeList: Array<{value: string, label: string, name?: string, userId?: string | number}>;
  qcList: Array<{value: string, label: string, name?: string, userId?: string | number}>;
  handleQcSignatureChange?: (signature: string | null) => void;
  handleCustomerSignatureChange?: (signature: string | null) => void;
  onAddComment?: (comment: { signature: string; name: string; comment?: string; type: 'customer' | 'qc' }) => void;
  signatureData?: any;
  onSignatureChange?: (comment: { signature: string; name: string; comment?: string; stampImage?: string; type: 'customer' | 'qc' }) => void;
  canSign?: boolean;
  canEdit?: boolean;
  isEditMode?: boolean;
  isCustomerRepresentativeUser?: boolean;
  isQCInspectorUser?: boolean;
  isWelderUser?: boolean;
  currentUserTeamMemberId?: string | number;
  isAdminOrSuperAdmin?: boolean;
  /** Hide the Customer Representative column entirely */
  showCustomerRep?: boolean;
  /** Hide the QC Inspector column entirely */
  showQc?: boolean;
  /** Override the label shown above the customer-rep dropdown */
  customerRepLabel?: string;
  /** Override the label shown above the QC dropdown */
  qcLabel?: string;
  /** Override the placeholder text for the signature area */
  placeholder?: string;
  /** `vertical`: welder/rep column above QC; select + action buttons stack to avoid overlap in narrow panels. */
  align?: 'horizontal' | 'vertical';
}

export const Signature: React.FC<SignatureProps> = ({ 
  customerSignature, 
  qcSignature, 
  customerRepresentativeList,
  qcList,
  handleQcSignatureChange,
  handleCustomerSignatureChange,
  onAddComment,
  signatureData,
  onSignatureChange,
  canSign = true,
  canEdit = true,
  isEditMode = false,
  isCustomerRepresentativeUser = false,
  isQCInspectorUser = false,
  isWelderUser = false,
  currentUserTeamMemberId,
  isAdminOrSuperAdmin = false,
  showCustomerRep = true,
  showQc = true,
  customerRepLabel = 'Customer Representative',
  qcLabel = 'QC Inspector',
  placeholder = 'Sign here',
  align = 'horizontal',
}) => {
  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
  const [signatureDialogType, setSignatureDialogType] = useState<'customer' | 'qc'>('customer');
  const [customerSignatureData, setCustomerSignatureData] = useState<{ signature: string; name: string; comment?: string; stampImage?: string; timestamp: string } | null>(null);
  const [qcSignatureData, setQcSignatureData] = useState<{ signature: string; name: string; comment?: string; stampImage?: string; timestamp: string } | null>(null);

  // Restore signatures from saved data (clear each side when missing so the other party's block still shows).
  React.useEffect(() => {
    if (!signatureData?.signature) {
      setCustomerSignatureData(null);
      setQcSignatureData(null);
      return;
    }
    const { customer, qc } = signatureData.signature;
    if (customer) {
      setCustomerSignatureData(customer);
    } else {
      setCustomerSignatureData(null);
    }
    if (qc) {
      setQcSignatureData(qc);
    } else {
      setQcSignatureData(null);
    }
  }, [signatureData]);

  // Auto-select current user if they are Customer Representative or QC Inspector
  // React.useEffect(() => {
    
  //   console.log('customerRepresentativeList', customerRepresentativeList);
  //   console.log('qcList', qcList);
  //   console.log('currentUserTeamMemberId', currentUserTeamMemberId);
  //   console.log('isCustomerRepresentativeUser', isCustomerRepresentativeUser);
    
  //   if (currentUserTeamMemberId) {


  //     const currentUserTeamMemberIdStr = currentUserTeamMemberId.toString();
      
  //     // Auto-select Customer Representative if user is Customer Representative
  //     if (isCustomerRepresentativeUser && !customerSignature) {
  //       const currentUserInList = customerRepresentativeList.find(
  //         member => member.userId === currentUserTeamMemberIdStr
  //       );
  //       if (currentUserInList && handleCustomerSignatureChange) {
  //         handleCustomerSignatureChange(currentUserInList.value);
  //       }
  //     }
      
  //     // Auto-select QC Inspector if user is QC Inspector
  //     if (isQCInspectorUser && !qcSignature) {
  //       const currentUserInList = qcList.find(
  //         member => member.userId === currentUserTeamMemberIdStr
  //       );
  //       if (currentUserInList && handleQcSignatureChange) {
  //         handleQcSignatureChange(currentUserInList.value);
  //       }
  //     }
  //   }
  // }, [currentUserTeamMemberId , customerRepresentativeList, qcList, isCustomerRepresentativeUser, isQCInspectorUser]);


  const formatDate = (dateString: string) => {
    if (!dateString?.trim()) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleOpenSignatureDialog = (type: 'customer' | 'qc') => {
    setSignatureDialogType(type);
    setIsSignatureDialogOpen(true);
  };

  const handleSaveSignature = (signature: string, name: string, comment?: string, stampImage?: string) => {
    const signatureData = {
      signature,
      name,
      comment,
      stampImage,
      timestamp: new Date().toISOString()
    };
    
    if (signatureDialogType === 'customer') {
      setCustomerSignatureData(signatureData);
    } else {
      setQcSignatureData(signatureData);
    }

   // console.log(signatureData);
    
    // Call the onSignatureChange callback to save the signature data
    onSignatureChange?.({
      signature,
      name,
      comment,
      stampImage,
      type: signatureDialogType
    });
    
    onAddComment?.({
      signature,
      name,
      comment,
      type: signatureDialogType
    });
  };

  const handleDeleteSignature = (type: 'customer' | 'qc') => {
    if (type === 'customer') {
      setCustomerSignatureData(null);
      // Clear the signature from the parent component
      if (signatureData?.signature?.customer) {
        delete signatureData.signature.customer;
      }
    } else {
      setQcSignatureData(null);
      // Clear the signature from the parent component
      if (signatureData?.signature?.qc) {
        delete signatureData.signature.qc;
      }
    }
    
    // Notify parent component of the deletion
    onSignatureChange?.({
      signature: '',
      name: '',
      comment: '',
      type: type
    });
  };

  useEffect(() => {
    // console.log('customerSignature', customerSignature);
    // console.log('qcSignature', qcSignature);
    // console.log('currentUserTeamMemberId', currentUserTeamMemberId);
    // console.log('isCustomerRepresentativeUser', isCustomerRepresentativeUser);
    // console.log('isQCInspectorUser', isQCInspectorUser);
    // console.log('isWelderUser', isWelderUser);
    // console.log('isAdminOrSuperAdmin', isAdminOrSuperAdmin);
  }, [customerSignature, qcSignature]);

  const selectedCustomerRepUserId = customerRepresentativeList.find(
    (m) => m.value === customerSignature
  )?.userId;
  const selectedQcUserId = qcList.find((m) => m.value === qcSignature)?.userId;
  const isCustomerSelectionCurrentUser =
    currentUserTeamMemberId != null &&
    selectedCustomerRepUserId != null &&
    String(selectedCustomerRepUserId) === String(currentUserTeamMemberId);
  const isQcSelectionCurrentUser =
    currentUserTeamMemberId != null &&
    selectedQcUserId != null &&
    String(selectedQcUserId) === String(currentUserTeamMemberId);

  const isVertical = align === 'vertical';
  const twoColumns = showCustomerRep && showQc;
  const outerLayoutClass = isVertical
    ? 'grid grid-cols-1 gap-6'
    : `grid gap-4 md:gap-6 ${twoColumns ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`;
  const controlsRowClass = isVertical
    ? 'flex flex-col gap-2 w-full'
    : 'flex flex-row flex-wrap items-stretch gap-2 w-full';

  return (
    <div className="space-y-4 pt-6 border-t">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Signatures</h3>
      </div>
      <div className={outerLayoutClass}>
        {showCustomerRep && (
        <div className="space-y-2 min-w-0">
          <Label>{customerRepLabel}</Label>
          <div className={controlsRowClass}>
            <Select
              value={customerSignature || undefined}
              onValueChange={ handleCustomerSignatureChange }
              disabled={!canEdit || !isEditMode || (!isAdminOrSuperAdmin && !isCustomerRepresentativeUser)}
            >
              <SelectTrigger className="flex-1 w-full min-w-0" disabled={!canEdit || (!isAdminOrSuperAdmin && !isCustomerRepresentativeUser)}>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent>
                {customerRepresentativeList.length > 0 ? (
                  customerRepresentativeList.map(member => {
                    const isCurrentUser =
                      currentUserTeamMemberId != null &&
                      member.userId != null &&
                      String(member.userId) === String(currentUserTeamMemberId);
                    const isDisabled =
                      !isAdminOrSuperAdmin &&
                      !isCustomerRepresentativeUser &&
                      !isCurrentUser &&
                      member.value !== customerSignature;
                    return (
                      <SelectItem key={member.value} value={member.value} disabled={isDisabled}>
                        {member.label}
                      </SelectItem>
                    );
                  })
                ) : (
                  <SelectItem value="no-customer-rep" disabled>
                    No customer representatives available for this project
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          {customerSignature && canEdit && isEditMode && (
            <div className={`flex gap-2 shrink-0 ${isVertical ? 'flex-row flex-wrap w-full' : ''}`}>
              {canSign && (isAdminOrSuperAdmin || isCustomerRepresentativeUser || isCustomerSelectionCurrentUser) && (
                <Button
                  variant="default"
                  disabled={!customerSignature}
                  onClick={() => handleOpenSignatureDialog('customer')}
                  className={`flex items-center gap-2 whitespace-nowrap ${isVertical ? 'flex-1 min-w-0 sm:flex-initial' : ''}`}
                >
                  <FileSignature className="h-4 w-4" />
                  Add Signature
                </Button>
              )}
              <Button
                className="text-red-600 hover:text-red-700 hover:bg-red-50 w-8 h-8 p-0 flex items-center justify-center"
                onClick={() => handleDeleteSignature('customer')}
                title="Delete Customer Signature"
                variant="outline"
                disabled={!customerSignatureData || (!isAdminOrSuperAdmin && !isCustomerRepresentativeUser && !isCustomerSelectionCurrentUser)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
          </div>
          
          {customerSignatureData && customerSignatureData.signature && (
            <div className="mt-2 p-3 border rounded-md bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span className="text-sm text-gray-500">Signed by:</span>
                <span className="text-sm font-medium">{customerSignatureData.name}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-1">
                <span className="text-sm text-gray-500">Date:</span>
                <span className="text-sm">{formatDate(customerSignatureData.timestamp)}</span>
              </div>
              {customerSignatureData.comment && (
                <div className="mt-2">
                  <span className="text-sm text-gray-500">Comment:</span>
                  <p className="text-sm mt-1">{customerSignatureData.comment}</p>
                </div>
              )}
              <div className="mt-2 border rounded p-2 bg-white flex items-center justify-between gap-4">
                <img
                  src={customerSignatureData.signature}
                  alt={`${customerSignatureData.name} signature`}
                  className="max-h-16 w-auto"
                />
                {customerSignatureData.stampImage && (
                  <img
                    src={customerSignatureData.stampImage}
                    alt="Stamp image"
                    className="max-h-16 w-auto"
                  />
                )}
              </div>
            </div>
          )}
        </div>
        )}

        {showQc && (
        <div className="space-y-2 min-w-0">
          <Label>{qcLabel}</Label>
          <div className={controlsRowClass}>
            <Select
              value={qcSignature || undefined}
              onValueChange={handleQcSignatureChange}
              disabled={!canEdit || !isEditMode || (!isAdminOrSuperAdmin && !isQCInspectorUser)}
            >
              <SelectTrigger className="flex-1 w-full min-w-0" disabled={!canEdit || !isEditMode || (!isAdminOrSuperAdmin && !isQCInspectorUser)}>
                <SelectValue placeholder="Select QC inspector" />
              </SelectTrigger>
              <SelectContent>
                {qcList.length > 0 ? (
                  qcList.map(member => {
                    const isCurrentUser =
                      currentUserTeamMemberId != null &&
                      member.userId != null &&
                      String(member.userId) === String(currentUserTeamMemberId);
                    const isDisabled =
                      !isAdminOrSuperAdmin &&
                      !isQCInspectorUser &&
                      !isCurrentUser &&
                      member.value !== qcSignature;
                    return (
                      <SelectItem key={member.value} value={member.value} disabled={isDisabled}>
                        {member.label}
                      </SelectItem>
                    );
                  })
                ) : (
                  <SelectItem value="no-qc" disabled>
                    No QC inspectors available for this project
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {qcSignature && canEdit && isEditMode && (
              <div className={`flex gap-2 shrink-0 ${isVertical ? 'flex-row flex-wrap w-full' : ''}`}>
                {canSign && (isAdminOrSuperAdmin || isQCInspectorUser || isQcSelectionCurrentUser) && (
                  <Button
                    variant="default"
                    disabled={!qcSignature}
                    onClick={() => handleOpenSignatureDialog('qc')}
                    className={`flex items-center gap-2 whitespace-nowrap ${isVertical ? 'flex-1 min-w-0 sm:flex-initial' : ''}`}
                  >
                    <FileSignature className="h-4 w-4" />
                    Add Signature
                  </Button>
                )}
                <Button
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 w-8 h-8 p-0 flex items-center justify-center"
                  onClick={() => handleDeleteSignature('qc')}
                  title="Delete QC Signature"
                  variant="outline"
                  disabled={!qcSignatureData || (!isAdminOrSuperAdmin && !isQCInspectorUser && !isQcSelectionCurrentUser)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
                            </div>
          {qcSignatureData && qcSignatureData.signature && (
            <div className="mt-2 p-3 border rounded-md bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span className="text-sm text-gray-500">Signed by:</span>
                <span className="text-sm font-medium">{qcSignatureData.name}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-1">
                <span className="text-sm text-gray-500">Date:</span>
                <span className="text-sm">{formatDate(qcSignatureData.timestamp)}</span>
              </div>
              {qcSignatureData.comment && (
                <div className="mt-2">
                  <span className="text-sm text-gray-500">Comment:</span>
                  <p className="text-sm mt-1">{qcSignatureData.comment}</p>
                </div>
              )}
              <div className="mt-2 border rounded p-2 bg-white flex items-center justify-between gap-4">
                <img
                  src={qcSignatureData.signature}
                  alt={`${qcSignatureData.name} signature`}
                  className="max-h-16 w-auto"
                />
                {qcSignatureData.stampImage && (
                  <img
                    src={qcSignatureData.stampImage}
                    alt="Stamp image"
                    className="max-h-16 w-auto"
                  />
                )}
              </div>
            </div>
          )}
        </div>
        )}
      </div>


      {/* Signature Dialog */}
      <SignatureDialog
        isOpen={isSignatureDialogOpen}
        onClose={() => setIsSignatureDialogOpen(false)}
        onSave={handleSaveSignature}
        title={`Add ${signatureDialogType === 'customer' ? 'Customer' : 'QC'} Signature`}
        initialName={signatureDialogType === 'customer' 
          ? customerRepresentativeList.find(m => m.value === customerSignature)?.name || 
            customerRepresentativeList.find(m => m.value === customerSignature)?.label || ''
          : qcList.find(m => m.value === qcSignature)?.name || 
            qcList.find(m => m.value === qcSignature)?.label || ''
        }
        initialSignature={signatureDialogType === 'customer' 
          ? customerSignatureData?.signature || null
          : qcSignatureData?.signature || null
        }
        initialComment={signatureDialogType === 'customer' 
          ? customerSignatureData?.comment || ''
          : qcSignatureData?.comment || ''
        }
      />
    </div>
  );
};